import {
  Alert,
  AlertColor,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ReferralAnalyticsDashboard,
  ReferralTable,
  ReferralViewModal,
} from "../../components/backend/referral";
import {
  useReferralAnalytics,
  useReferrals,
} from "../../hooks/useReferral";
import { ReferralFilters } from "../../types/referral";

import AdminPage from "./AdminPage";

const ReferralAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ReferralFilters>({
    page: 1,
    pageSize: 20,
  });
  const [selectedReferralId, setSelectedReferralId] = useState<string | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "info" });

  const { data: analyticsResponse, isLoading: analyticsLoading } =
    useReferralAnalytics();
  const { data: referralsResponse, isLoading: referralsLoading } =
    useReferrals(filters);

  const analytics = analyticsResponse?.data;
  const referralsData = referralsResponse?.data;

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t("admin.referral.referralAnalytics")}
        </Typography>

        {analyticsLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : analytics ? (
          <ReferralAnalyticsDashboard analytics={analytics} />
        ) : null}

        <Box sx={{ mt: 3, mb: 2, display: "flex", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("admin.referral.status")}</InputLabel>
            <Select
              value={filters.status ?? ""}
              label={t("admin.referral.status")}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  status: e.target.value === "" ? undefined : Number(e.target.value),
                  page: 1,
                }))
              }
            >
              <MenuItem value="">{t("common.all")}</MenuItem>
              <MenuItem value={1}>{t("admin.referral.pending")}</MenuItem>
              <MenuItem value={2}>{t("admin.referral.completed")}</MenuItem>
              <MenuItem value={3}>{t("admin.referral.expired")}</MenuItem>
              <MenuItem value={4}>{t("admin.referral.cancelled")}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {referralsLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : referralsData ? (
          <ReferralTable
            referrals={referralsData.items}
            totalCount={referralsData.totalCount}
            page={filters.page ?? 1}
            pageSize={filters.pageSize ?? 20}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            onViewDetail={(id) => setSelectedReferralId(id)}
          />
        ) : null}

        <ReferralViewModal
          open={!!selectedReferralId}
          onClose={() => setSelectedReferralId(null)}
          referralId={selectedReferralId}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default ReferralAnalyticsPage;
